import { Flex, For, Avatar } from 'shared';
import { ReactionEmojiPicker, ReactionsButton } from 'shared_DEPRECATED/components/Reactions';
import { DEFAULT_REACTIONS_OPTIONS } from 'shared_DEPRECATED/components/Reactions/config';
import { ReactionsType } from 'shared_DEPRECATED/components/Reactions/types';

import { User } from 'features/user/User';

type ChatMessageReactionsViewProps = {
  reactions: ReactionsType;
  onAddReaction: (reaction: string) => void;
  onRemoveReaction: (reaction: string) => void;
  user: User;
  emojiPickerPosition?: 'start' | 'end';
};

export const ChatMessageReactionsView = ({
  reactions,
  onAddReaction,
  onRemoveReaction,
  user,
  emojiPickerPosition = 'end',
}: ChatMessageReactionsViewProps) => {
  const reactionEntries = Object.entries(reactions);

  const handleReactionClick = (reaction: string) => {
    const reactionUsers = reactions[reaction];
    const hasMyReaction = reactionUsers?.some(
      (r) =>
        r.email === user.email &&
        r.firstName === user.firstName &&
        r.lastName === user.lastName
    );

    if (hasMyReaction) {
      onRemoveReaction(reaction);
    } else {
      onAddReaction(reaction);
    }
  };

  // Get all unique users who reacted
  const allReactingUsers = reactionEntries.reduce((acc, [_, users]) => {
    users.forEach((u) => {
      const key = `${u.email}-${u.firstName}-${u.lastName}`;
      if (!acc.has(key)) {
        acc.set(key, u);
      }
    });
    return acc;
  }, new Map());

  const uniqueUsers = Array.from(allReactingUsers.values());

  if (reactionEntries.length === 0) {
    return (
      <Flex alignItems="center" gap="4px">
        <ReactionEmojiPicker
          handleEmojiSelect={(reaction) => onAddReaction(reaction)}
          options={{
            ...DEFAULT_REACTIONS_OPTIONS,
            emojiSize: 20,
          }}
        />
      </Flex>
    );
  }

  return (
    <Flex
      alignItems="center"
      gap="4px"
      direction={emojiPickerPosition === 'start' ? 'row-reverse' : 'row'}
    >
      <Flex
        gap="4px"
        flexWrap="wrap"
        alignItems="center"
        bg="var(--bgCompSecondaryMid)"
        borderRadius="var(--border-radius16)"
        p="4px"
      >
        <For each={reactionEntries}>
          {([reaction, users]) => {
            return (
              <Flex key={reaction} alignItems="center" gap="2px">
                <ReactionsButton
                  onClick={() => handleReactionClick(reaction)}
                  dataTestid="chat-message-reaction-button"
                >
                  {reaction}
                </ReactionsButton>
                {users.length > 1 && (
                  <span style={{ fontSize: '12px' }}>{users.length}</span>
                )}
              </Flex>
            );
          }}
        </For>
        <Flex align="center" gap="2px">
          <For each={uniqueUsers.slice(0, 3)}>
            {(reactingUser) => (
              <Avatar
                key={reactingUser.email}
                src={reactingUser.picture.url}
                name={reactingUser.name}
                size="2xs"
              />
            )}
          </For>
          {uniqueUsers.length > 3 && (
            <span style={{ fontSize: '12px', marginLeft: '2px' }}>
              +{uniqueUsers.length - 3}
            </span>
          )}
        </Flex>
      </Flex>
      <ReactionEmojiPicker
        handleEmojiSelect={(reaction) => handleReactionClick(reaction)}
        options={{
          ...DEFAULT_REACTIONS_OPTIONS,
          emojiSize: 20,
        }}
      />
    </Flex>
  );
};
